import React from "react";
import { Navigate } from "react-router-dom";
import AppLoader from "../components/AppLoader";
import { useAuth } from "../context/AuthContext";
import { USER_ROLES } from "../types";
import { ROUTES } from "./config";
import { useSearchParam } from "react-use";

type Props = {
    children?: React.ReactNode;
    forUserAuthType: "logged-in" | "logged-out";
} & (
    | { allowedRoles?: USER_ROLES[]; disallowedRoles?: never }
    | { allowedRoles?: never; disallowedRoles?: USER_ROLES[] }
    | { allowedRoles?: never; disallowedRoles?: never }
);

const RoutesGuard = ({
    children,
    forUserAuthType,
    allowedRoles,
    disallowedRoles,
}: Props) => {
    const { user, loading } = useAuth();
    const redirectTo = useSearchParam("redirect");

    const loginPath = `/${ROUTES.LOGIN.path}`;
    const defaultAppPath =
        user?.role === USER_ROLES.LOCAL_ADMIN
            ? `/${ROUTES.LOCAL_ADMIN_DASHBOARD.path}`
            : redirectTo && redirectTo !== "/"
            ? redirectTo
            : `/${ROUTES.OPEN_TEST.path}`;

    // still loading user state
    if (loading) {
        return <AppLoader fullPage />;
    }
    // not logged in
    if (!user) {
        return forUserAuthType === "logged-out" ? (
            <>{children}</>
        ) : (
            <Navigate to={loginPath} />
        );
    }
    // logged in
    return forUserAuthType === "logged-in" ? (
        (allowedRoles && !allowedRoles.includes(user.role)) ||
        (disallowedRoles && disallowedRoles.includes(user.role)) ? (
            // Redirect to default app path if user role is not allowed
            <Navigate to={defaultAppPath} />
        ) : (
            <>{children}</>
        )
    ) : (
        // Redirect to default app path if user is logged-in
        <Navigate to={defaultAppPath} />
    );
};

export default RoutesGuard;
